/**
 * The admin portal's pipeline tab (WP1.3): the ledger, and the dead-letter list.
 *
 * A queue that fails quietly is worse than no queue, so the failures are the main content here —
 * each with the error that stopped it and the two choices a human has: try again, or give up.
 * Neither writes a word of content; the pipeline only ever produces proposals.
 */

import { useCallback, useEffect, useState } from "react";

import type { PipelineOverview } from "../../api";
import { api, ApiError } from "../../api";
import { EmptyState } from "../../components/EmptyState";
import { Button, Card, Num } from "../../components/primitives";
import { SkeletonText } from "../../components/Skeleton";
import { useToast } from "../../components/toast";
import { formatWhen } from "../../time";

const STATES = [
  { key: "pending", label: "Väntar" },
  { key: "running", label: "Kör" },
  { key: "succeeded", label: "Klara" },
  { key: "failed", label: "Försöker igen" },
  { key: "dead", label: "Dödsbrevlåda" },
  { key: "discarded", label: "Avskrivna" },
] as const;

export function PipelineSection() {
  const showToast = useToast();
  const [overview, setOverview] = useState<PipelineOverview | null>(null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);

  const errorToast = useCallback(
    (error: unknown, fallback: string) => {
      showToast({ message: error instanceof ApiError ? error.message : fallback, tone: "fail" });
    },
    [showToast],
  );

  const reload = useCallback(() => {
    void api
      .pipelineOverview()
      .then((data) => {
        setOverview(data);
        setFailed(false);
      })
      .catch((error) => {
        errorToast(error, "Pipelineöversikten kunde inte hämtas.");
        setFailed(true);
      });
  }, [errorToast]);

  useEffect(() => reload(), [reload]);

  const runNow = async () => {
    setBusy(true);
    try {
      const report = await api.pipelineRun();
      showToast({
        message:
          report.claimed === 0
            ? "Inget väntade."
            : `${report.claimed} jobb kördes, ${report.failed} misslyckades, ` +
              `${report.proposals} nya förslag.`,
        tone: report.failed > 0 ? "warn" : "pass",
      });
      reload();
    } catch (error) {
      errorToast(error, "Pipelinen kunde inte köras.");
    } finally {
      setBusy(false);
    }
  };

  const act = async (jobId: string, action: "retry" | "discard") => {
    setBusy(true);
    try {
      await (action === "retry" ? api.pipelineRetry(jobId) : api.pipelineDiscard(jobId));
      showToast({
        message: action === "retry" ? "Jobbet ligger i kön igen." : "Jobbet är avskrivet.",
        tone: "pass",
      });
      reload();
    } catch (error) {
      errorToast(error, "Åtgärden gick inte igenom.");
    } finally {
      setBusy(false);
    }
  };

  if (failed) {
    return (
      <EmptyState
        title="Pipelinen kunde inte läsas"
        description="Kontrollera att API:t svarar och att du är administratör."
      />
    );
  }
  if (overview === null) {
    return <SkeletonText lines={6} />;
  }

  return (
    <div className="grid gap-s5">
      <Card className="grid gap-s4">
        <span className="nb-overline">Jobb</span>
        <ul className="grid gap-s2">
          {STATES.map((state) => (
            <li
              key={state.key}
              className="flex flex-wrap justify-between gap-s3 border-b border-line pb-s2 last:border-b-0"
            >
              <span className="nb-body">{state.label}</span>
              <span className="nb-body-muted">
                <Num>{overview.stats[state.key]}</Num>
              </span>
            </li>
          ))}
        </ul>
        <p className="nb-body-muted">
          <Num>{overview.stats.proposals_pending}</Num> förslag väntar på granskning.
          Hanterare: {overview.handlers.join(", ") || "inga"}.
        </p>
        <div>
          <Button variant="ghost" disabled={busy} onClick={() => void runNow()}>
            {busy ? "Kör …" : "Kör pipelinen nu"}
          </Button>
        </div>
        <p className="nb-body-muted">
          Pipelinen körs annars av sig själv varje minut. Den skriver bara förslag — innehållet
          rörs aldrig utan att en människa accepterat.
        </p>
      </Card>

      <Card className="grid gap-s4">
        <span className="nb-overline">Dödsbrevlåda</span>
        {overview.dead_letters.length === 0 ? (
          <p className="nb-body-muted">Inget har fastnat.</p>
        ) : (
          <ul className="grid gap-s4">
            {overview.dead_letters.map((job) => (
              <li key={job.id} className="grid gap-s2 border-b border-line pb-s4 last:border-b-0">
                <div className="flex flex-wrap items-baseline gap-s3">
                  <span className="nb-body font-medium">{job.kind}</span>
                  <span className="nb-body-muted">
                    {job.source_type} · <Num>{job.attempts}</Num> av{" "}
                    <Num>{job.max_attempts}</Num> försök
                  </span>
                  <span className="ml-auto nb-body-muted">{formatWhen(job.updated_at)}</span>
                </div>
                {job.last_error && (
                  <p className="nb-body-muted break-words font-mono text-small">
                    {job.last_error}
                  </p>
                )}
                <div className="flex flex-wrap gap-s3">
                  <Button variant="ghost" disabled={busy} onClick={() => void act(job.id, "retry")}>
                    Försök igen
                  </Button>
                  <Button
                    variant="ghost"
                    disabled={busy}
                    onClick={() => void act(job.id, "discard")}
                  >
                    Skriv av
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
